<script setup>
/**
 * Modals
 * This component acts as a global modal manager, rendering dynamic modal components
 * based on the state of the Pinia 'modals' store. It handles modal stacking,
 * backdrop, and closing modals via the Escape key or clicking outside.
 */
import {computed, onMounted, onBeforeUnmount} from 'vue'
import {useModalsStore} from '@/stores/modals'

// Import All Modal Components
import AddContentModal from './AddContentModal.vue'
import AddItemModal from './AddItemModal.vue'
import BulkUploadModal from './BulkUploadModal.vue'
import CodeMirrorModal from './CodeMirrorModal.vue'
import ConfirmModal from './ConfirmModal.vue'
import ContentTypeFieldsModal from '@/common/content-type/ContentTypeFieldsModal.vue'
import HistoryCodeEditorModal from '@/common/item-history/HistoryCodeEditorModal.vue'
import HistoryTexteditorModal from '@/common/item-history/HistoryTexteditorModal.vue'
import ImportModal from './ImportModal.vue'
import LightboxModal from './LightboxModal.vue'
import ListingColumnsModal from './ListingColumnsModal.vue'
import NewContentTypeModal from '@/common/content-type/NewContentTypeModal.vue'
import OdpAddPaymentModal from '@/bespoke/odp/OdpAddPaymentModal.vue'
import OdpAddCreditModal from '@/bespoke/odp/OdpAddCreditModal.vue'
import OdpCreditHistoryModal from '@/bespoke/odp/OdpCreditHistoryModal.vue'
import OdpDeleteCreditModal from '@/bespoke/odp/OdpDeleteCreditModal.vue'
import OdpPaymentLogModal from '@/bespoke/odp/OdpPaymentLogModal.vue'
import OdpPaymentNoteModal from '@/bespoke/odp/OdpPaymentNoteModal.vue'
import OrdersLogsModal from './OrdersLogsModal.vue'
import OrdersReportsModal from './OrdersReportsModal.vue'
import PrintReportModal from '@/components/reports/PrintReportModal.vue'
import SelectListModal from './SelectListModal.vue'
import ShowNotesModal from '@/bespoke/flyspain/ShowNotesModal.vue'
import SiteTreeModal from './SiteTreeModal.vue'
import TagsModal from '@/common/tags/TagsModal.vue'
import ThemesEditorAddModal from './ThemesEditorAddModal.vue'
import ThemesFindModal from './ThemesFindModal.vue'
import ThemesUsageModal from './ThemesUsageModal.vue'
import ZendeskModal from './ZendeskModal.vue'

// Pinia Store
const modalsStore = useModalsStore()

// Create a map of all imported components for dynamic rendering
const componentsMap = {
    AddContentModal,
    AddItemModal,
    BulkUploadModal,
    CodeMirrorModal,
    ConfirmModal,
    ContentTypeFieldsModal,
    HistoryCodeEditorModal,
    HistoryTexteditorModal,
    ImportModal,
    LightboxModal,
    ListingColumnsModal,
    NewContentTypeModal,
    OdpAddPaymentModal,
    OdpAddCreditModal,
    OdpCreditHistoryModal,
    OdpDeleteCreditModal,
    OdpPaymentLogModal,
    OdpPaymentNoteModal,
    OrdersLogsModal,
    OrdersReportsModal,
    PrintReportModal,
    SelectListModal,
    ShowNotesModal,
    SiteTreeModal,
    TagsModal,
    ThemesEditorAddModal,
    ThemesFindModal,
    ThemesUsageModal,
    ZendeskModal,
}

// --- Computed ---
const modals = computed(() => modalsStore.modals)

// --- Methods ---
const hideModal = (index) => {
    modalsStore.remove(index)
}

const handleEscKey = (e) => {
    if (e.key === 'Escape' && modals.value.length > 0) {
        modalsStore.pop()
    }
}

// --- Lifecycle ---
onMounted(() => {
    window.addEventListener('keydown', handleEscKey)
})

onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleEscKey)
})
</script>

<template>
    <div id="modal-container">
        <div
            v-for="modal in modals"
            :key="modal.id"
            class="modal fade show"
            :class="{ 'd-block': modal.show }"
            role="dialog"
            tabindex="-1"
            @click.self="hideModal(modal.id)"
        >
            <component
                :is="componentsMap[modal.componentName]"
                :modal="modal"
                :options="modal.options"
                @hide-modal="hideModal(modal.id)"
            />
        </div>

        <div v-if="modals.length > 0" class="modal-backdrop fade show"></div>
    </div>
</template>

<style scoped>
/* Ensure Bootstrap 5 "show" state is respected without jQuery */
.modal.show {
    background-color: rgba(0, 0, 0, 0.3);
}

.modal-backdrop.show {
    opacity: 0.5;
}

/* Fix for multiple modals stacking */
.modal {
    z-index: 1060;
}
</style>